import React, { lazy, Suspense } from 'react';
import { ClerkProvider } from '@clerk/react-router';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';

import env from '@/configs/env';
import PATH from '@/constants/path';
import PrivacyPolicy from '@/pages/privacy-policy';
import SuspensePage from '@/pages/suspense/index';
import TermsOfService from '@/pages/terms-of-service';

import ProtectedLayout from './protected-layout';
import PublicLayout from './public-layout';

// Lazy load page, except for layouts and the Loading page
const Website = lazy(() => import('@/pages/website'));
const NotFound = lazy(() => import('@/pages/not-found'));
const ReportList = lazy(() => import('@/pages/report-list'));
const NewReport = lazy(() => import('@/pages/new-report'));
const Financial = lazy(() => import('@/pages/financial'));
const Report = lazy(() => import('@/pages/report'));

const Routes = (): React.JSX.Element => {
  return (
    <RouterProvider
      router={createBrowserRouter([
        {
          element: (
            <ClerkProvider
              publishableKey={env.VITE_CLERK_PUBLISHABLE_KEY}
              appearance={{
                elements: {
                  button: 'w-auto h-auto px-4 py-2',
                },
              }}
            >
              <Outlet />
            </ClerkProvider>
          ),
          children: [
            {
              element: <PublicLayout />,
              children: [
                {
                  path: PATH.INDEX,
                  element: (
                    <Suspense fallback={<SuspensePage />}>
                      <Website />
                    </Suspense>
                  ),
                },
                {
                  path: PATH.PRIVACY_POLICY,
                  element: (
                    <Suspense fallback={<SuspensePage />}>
                      <PrivacyPolicy />
                    </Suspense>
                  ),
                },
                {
                  path: PATH.TERMS_OF_SERVICE,
                  element: (
                    <Suspense fallback={<SuspensePage />}>
                      <TermsOfService />
                    </Suspense>
                  ),
                },
                {
                  element: (
                    <Suspense fallback={<SuspensePage />}>
                      <NotFound />
                    </Suspense>
                  ),
                  path: PATH.NOT_FOUND,
                },
              ],
            },
            {
              element: <ProtectedLayout />,
              children: [
                {
                  path: PATH.DASHBOARD.INDEX,
                  element: (
                    <Suspense fallback={<SuspensePage />}>
                      <ReportList />
                    </Suspense>
                  ),
                },
                {
                  path: PATH.DASHBOARD.NEW,
                  element: (
                    <Suspense fallback={<SuspensePage />}>
                      <NewReport />
                    </Suspense>
                  ),
                },
                {
                  path: PATH.DASHBOARD.REPORT(':id'),
                  element: (
                    <Suspense fallback={<SuspensePage />}>
                      <Report />
                    </Suspense>
                  ),
                },
                {
                  path: PATH.DASHBOARD.FINANCIAL,
                  element: (
                    <Suspense fallback={<SuspensePage />}>
                      <Financial />
                    </Suspense>
                  ),
                },
              ],
            },
          ],
        },
      ])}
    />
  );
};

export default Routes;
